import { useEffect, useRef, useState } from 'react';
import { entreeRange, estComplete, quitteLaLigne, issueDeLaSaisie, type Bornes, type SorteDeSaisie } from './saisieTapee';
import './Saisies.css';

/**
 * Les primitives de saisie de la fiche — et elles existent pour tenir UNE règle.
 *
 * **On lit d'abord, on écrit ensuite.** Une fiche pleine de cadres de saisie est un
 * formulaire ; une fiche qui se lit est une fiche. Chaque valeur se lit en clair, s'ouvre en
 * champ quand on la touche, et se referme dès qu'on la quitte — c'est la même mécanique pour
 * une date, un choix, une bascule ou un texte, et elle ne doit donc s'écrire qu'une fois.
 *
 * Elles ne connaissent aucun champ de `Bulle` : elles reçoivent une valeur et rendent une
 * valeur. C'est ce qui permet à « quand », « ce que c'est » et aux paramètres de se lire
 * chacun comme un bloc, sans redire comment on saisit.
 */

/** Le nom à gauche, la valeur à droite, et l'aide qui dit pourquoi ce champ existe. */
export function Ligne({
  nom,
  aide,
  children,
}: {
  nom: string;
  aide?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="champs-ligne" data-tuto="champs-ligne">
      <div className="champs-nom" data-tuto="champs-nom">
        {nom}
        {aide && <span className="champs-aide">{aide}</span>}
      </div>
      <div className="champs-valeur" data-tuto="champs-valeur">{children}</div>
    </div>
  );
}

/**
 * Une ligne qui se lit et s'ouvre au clic : le nom, ce que ça dit, et à droite ce qui reste
 * à faire.
 *
 * L'action de droite est en accent tant que rien n'est posé ; une fois posé, il n'y a plus
 * rien à proposer — seulement à dire d'où la valeur vient.
 */
export function LigneQuiSOuvre({
  nom,
  valeur,
  vide,
  mention,
  action,
  ouvertAuDepart = false,
  onFini,
  plus,
  tuto,
  children,
}: {
  nom: string;
  valeur: string | null;
  vide: string;
  mention: string;
  action: string;
  /** Ouverte dès qu'elle paraît : la fiche la pose par « + Échéance » (ticket 02 de revue-design). */
  ouvertAuDepart?: boolean;
  /** La ligne se range (Entrée, ou on la quitte) : la fiche la retire si rien n'a été posé. */
  onFini?: () => void;
  /**
   * Vide, la ligne ne dit qu'un « + Une échéance » en pointillé, qui ouvre sa saisie sur la ligne
   * (le panneau Réglages de l'ordinateur, chantier reglages-de-la-fiche, modification 02).
   */
  plus?: string;
  /** Le repère du tuto posé sur la ligne. */
  tuto?: string;
  children: React.ReactNode;
}) {
  const [ouvert, setOuvert] = useState(ouvertAuDepart);
  const fini = () => {
    setOuvert(false);
    onFini?.();
  };

  if (plus && !ouvert && !valeur) {
    return (
      <div className="quand-ligne" data-tuto={tuto}>
        <span className="quand-nom">{nom}</span>
        <button type="button" className="quand-plus" onClick={() => setOuvert(true)}>
          {plus}
        </button>
      </div>
    );
  }

  return (
    <div className="quand-ligne" data-tuto={tuto}>
      <span className="quand-nom">{nom}</span>

      {ouvert ? (
        // Plus de « Fini » (retours d'Igor du 2026-10-03) : Entrée dans un de ses champs, ou
        // quitter la ligne, la range. Le champ a déjà écrit sa valeur (`ChampTape`, sur Entrée
        // et à la sortie) ; passer d'un de ses champs à l'autre (le rendez-vous, sa durée) ne
        // la range pas.
        <span
          className="quand-saisie"
          onKeyDown={(e) => {
            const cible = e.target as HTMLElement;
            if (entreeRange(e.key, cible.tagName, e.nativeEvent.isComposing)) fini();
          }}
          onBlur={(e) => {
            if (quitteLaLigne(e.currentTarget, e.relatedTarget)) fini();
          }}
        >
          {children}
        </span>
      ) : (
        <button
          type="button"
          className={`quand-valeur${valeur ? '' : ' est-vide'}`}
          onClick={() => setOuvert(true)}
        >
          {valeur ?? vide}
        </button>
      )}

      {ouvert ? null : valeur ? (
        <span className="quand-mention">{mention}</span>
      ) : (
        <button type="button" className="quand-action" onClick={() => setOuvert(true)}>
          {action}
        </button>
      )}
    </div>
  );
}

/** Un choix qui se retire en le recliquant : rien n'est obligatoire. */
export function Choix<T extends string>({
  valeur,
  options,
  onChoisir,
}: {
  valeur: T | null;
  /** `[valeur, mot]`, dans l'ordre de l'écran. */
  options: readonly (readonly [T, string])[];
  onChoisir: (valeur: T | null) => void;
}) {
  return (
    <div className="champs-choix">
      {options.map(([cle, nom]) => (
        <button
          key={cle}
          type="button"
          className={valeur === cle ? 'est-choisi' : ''}
          onClick={() => onChoisir(valeur === cle ? null : cle)}
        >
          {nom}
        </button>
      ))}
    </div>
  );
}

/**
 * Trois états, pas deux. `null` veut dire « tu n'as rien dit », et l'app ne suppose rien :
 * la bulle reste alors HORS du filtre « c'est ouvert maintenant », au lieu d'y entrer par
 * défaut (SPEC §3).
 */
export function Bascule({
  valeur,
  nom,
  onBasculer,
}: {
  valeur: boolean | null;
  nom: string;
  onBasculer: (valeur: boolean | null) => void;
}) {
  return (
    <button
      type="button"
      className={`champs-bascule${valeur === true ? ' est-choisi' : ''}`}
      onClick={() => onBasculer(valeur === true ? null : true)}
    >
      {nom}
    </button>
  );
}

/** Un champ texte qui n'envoie qu'à la sortie : on ne parle pas au serveur à chaque frappe. */
export function Texte({
  valeur,
  place,
  auto,
  onChanger,
  onFini,
}: {
  valeur: string | null;
  place: string;
  auto?: boolean;
  onChanger: (valeur: string | null) => void;
  onFini?: () => void;
}) {
  const [brouillon, setBrouillon] = useState(valeur ?? '');
  useEffect(() => setBrouillon(valeur ?? ''), [valeur]);

  return (
    <input
      className="champs-texte"
      value={brouillon}
      placeholder={place}
      autoFocus={auto}
      onChange={(e) => setBrouillon(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
      }}
      onBlur={() => {
        const propre = brouillon.trim();
        if (propre !== (valeur ?? '')) onChanger(propre || null);
        onFini?.();
      }}
    />
  );
}

const TYPE_DU_CHAMP: Record<SorteDeSaisie, string> = {
  jour: 'date',
  instant: 'datetime-local',
  nombre: 'number',
};

/**
 * Une date, une heure ou un nombre : ce qu'on tape au clavier ne s'écrit qu'entier, à la sortie
 * du champ ou sur Entrée ; ce qu'on choisit dans le calendrier du navigateur (ou aux flèches de
 * la souris) s'écrit aussitôt. Échap rend la valeur d'avant, sans fermer la fiche.
 *
 * La frappe se reconnaît à la touche : une touche pressée dans le champ met la saisie « au
 * clavier » jusqu'à la sortie ; un appui du pointeur sur le champ (ouvrir le calendrier) l'en
 * sort. Le calendrier du navigateur ne passe pas par les touches du champ. La règle elle-même
 * (complète ou non, écrire ou revenir) est dans `saisieTapee.ts`.
 *
 * `valeur` et `onEcrire` parlent la langue du champ (« 2026-10-15 », « 2026-10-15T14:30 »,
 * « 10 ») ; vide veut dire « retirer ».
 */
export function ChampTape({
  sorte,
  valeur,
  bornes,
  onEcrire,
  ...attributs
}: {
  sorte: SorteDeSaisie;
  valeur: string;
  bornes?: Bornes;
  onEcrire: (valeur: string) => void;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'onKeyDown' | 'onPointerDown' | 'min' | 'max' | 'step'
>) {
  const [brouillon, setBrouillon] = useState(valeur);
  const auClavier = useRef(false);
  /* La dernière valeur envoyée : la sortie du champ ne la renvoie pas avant que `valeur` suive. */
  const envoye = useRef<string | null>(null);

  useEffect(() => {
    setBrouillon(valeur);
    envoye.current = null;
  }, [valeur]);

  function ecrire(v: string) {
    if (v === valeur || v === envoye.current) return;
    envoye.current = v;
    onEcrire(v);
  }

  /* Le champ reprend la valeur d'avant, jusque dans ses cases à moitié tapées. */
  function rendre(champ: HTMLInputElement) {
    setBrouillon(valeur);
    champ.value = valeur;
  }

  function finir(champ: HTMLInputElement) {
    auClavier.current = false;
    const issue = issueDeLaSaisie(sorte, champ.value, valeur, bornes, champ.validity.badInput);
    if (issue.sorte === 'ecrire') ecrire(issue.valeur);
    else if (issue.sorte === 'revenir') rendre(champ);
  }

  return (
    <input
      {...attributs}
      type={TYPE_DU_CHAMP[sorte]}
      min={bornes?.min}
      max={bornes?.max}
      step={bornes?.pas}
      value={brouillon}
      onPointerDown={() => {
        auClavier.current = false;
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finir(e.currentTarget);
        } else if (e.key === 'Escape') {
          // Échap n'annule que la saisie : sans ceci, il remonte jusqu'à la fenêtre, où la
          // fiche le prend pour elle et se ferme.
          e.stopPropagation();
          auClavier.current = false;
          rendre(e.currentTarget);
        } else if (e.key !== 'Tab') {
          auClavier.current = true;
        }
      }}
      onChange={(e) => {
        const v = e.target.value;
        setBrouillon(v);
        if (!auClavier.current && estComplete(sorte, v, bornes)) ecrire(v);
      }}
      onBlur={(e) => finir(e.currentTarget)}
    />
  );
}
